import { matchesMirrorCalendarReceiptOrigin, type MirrorCalendarReceiptReference } from "@/lib/calendarAction";

interface SessionResult {
  data: { session: { access_token: string; user: { id: string } } | null };
  error: unknown;
}

/** Structural subset so the production Supabase client and synthetic fixtures
 * use the same transport without importing a global authenticated client.
 */
export interface MirrorCalendarReceiptClient {
  auth: { getSession(): Promise<SessionResult> };
  functions: {
    invoke(name: string, options: {
      headers: Record<string, string>;
      body: Record<string, unknown>;
    }): Promise<{ data: unknown; error: unknown }>;
  };
}

export interface ConfirmMirrorCalendarReceiptOptions {
  client: MirrorCalendarReceiptClient;
  receipt: MirrorCalendarReceiptReference;
  language: string;
  isCurrent: () => boolean;
}

const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const nonempty = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0 && value.length <= 500 && !value.includes("\u0000");

/** Confirm a durable server receipt without replaying any provider write.
 * The bearer and original conversation stay pinned while the request runs.
 * A failed or obsolete result produces no acknowledgement in another session.
 */
export async function confirmMirrorCalendarReceipt({
  client, receipt, language, isCurrent,
}: ConfirmMirrorCalendarReceiptOptions): Promise<{ id: string; content: string } | null> {
  try {
    if (!isCurrent() || !object(receipt) || !nonempty(receipt.userId) ||
      !nonempty(receipt.sessionId) || !nonempty(receipt.action_id) || !nonempty(language)) return null;
    // Snapshot only the locator. Client-side success flags, event content and
    // arbitrary fields are never sent as evidence to the receipt-only path.
    const reference: MirrorCalendarReceiptReference = {
      provider: receipt.provider, capability: receipt.capability,
      idempotency_key: receipt.idempotency_key, action_id: receipt.action_id,
      userId: receipt.userId, sessionId: receipt.sessionId,
    };
    if (!matchesMirrorCalendarReceiptOrigin(reference, {
      userId: reference.userId, sessionId: reference.sessionId, actionId: reference.action_id,
    })) return null;

    if (!isCurrent()) return null;
    const before = await client.auth.getSession();
    if (!isCurrent() || before.error || before.data.session?.user.id !== reference.userId ||
      !nonempty(before.data.session.access_token)) return null;
    const token = before.data.session.access_token;

    if (!isCurrent()) return null;
    const result = await client.functions.invoke("mirror-chat", {
      headers: { Authorization: "Bearer " + token },
      body: {
        promptType: "chat", sessionId: reference.sessionId,
        userMessage: "[Calendar receipt confirmation]", language,
        calendar_receipt: {
          provider: reference.provider, capability: reference.capability,
          idempotency_key: reference.idempotency_key, action_id: reference.action_id,
        },
      },
    });
    if (!isCurrent() || result.error) return null;
    const data = result.data;
    if (!object(data) || data.ok !== true || data.error != null ||
      typeof data.response !== "string" || !data.response.trim() ||
      data.session_id !== reference.sessionId || !nonempty(data.message_id) ||
      (data.user_id !== undefined && data.user_id !== reference.userId) ||
      !Array.isArray(data.proposals) || data.proposals.length !== 0 ||
      !Array.isArray(data.action_results) || !data.action_results.some(action =>
        object(action) && action.ok === true && action.confirmed === true &&
        action.status === "succeeded" && action.scope === "external" &&
        action.evidence === "connector_receipt" && action.operation === "verify_calendar_receipt" &&
        action.provider === reference.provider && action.capability === reference.capability &&
        action.action_id === reference.action_id &&
        (action.user_id === undefined || action.user_id === reference.userId) &&
        (action.session_id === undefined || action.session_id === reference.sessionId) &&
        (action.idempotency_key === undefined || action.idempotency_key === reference.idempotency_key)
      )) return null;

    const acknowledgement = { id: data.message_id, content: data.response };
    if (!isCurrent()) return null;
    const after = await client.auth.getSession();
    if (!isCurrent() || after.error || after.data.session?.user.id !== reference.userId ||
      !nonempty(after.data.session.access_token)) return null;
    return acknowledgement;
  } catch {
    // Expected unavailable confirmations remain recoverable in the modal.
    // Never log the bearer, calendar contents, receipt payload or provider error.
    console.warn("[mirror] Calendar receipt confirmation unavailable");
    return null;
  }
}
